import Phaser from 'phaser';
import { frameKey, heroFrameSet, preloadFrameSet } from './assets';
import { HEROES, HeroId } from './heroes';

const PORTRAIT_R = 44;
const NATIVE_SPRITE = 92;
const HERO_ORDER: HeroId[] = [
  'captain-america',
  'iron-man',
  'black-panther',
  'spider-man',
  'thor',
  'hulk',
  'doctor-strange',
  'scarlet-witch',
  'captain-marvel',
  'vision',
  'ant-man',
  'ghost-spider',
  'spin',
  'wolverine',
  'rocket',
];

type Confetto = {
  g: Phaser.GameObjects.Graphics;
  vx: number;
  vy: number;
  rot: number;
  vrot: number;
};

export class EndingScene extends Phaser.Scene {
  private confetti: Confetto[] = [];

  constructor() {
    super('Ending');
  }

  preload() {
    for (const id of HERO_ORDER) {
      preloadFrameSet(this, heroFrameSet(id, 'idle'));
    }
  }

  create() {
    this.cameras.main.setBackgroundColor('#0a0f1f');
    this.drawStarField(this.scale.width, this.scale.height);
    this.layout();
    this.scale.on('resize', () => {
      this.children.removeAll(true);
      this.confetti = [];
      this.drawStarField(this.scale.width, this.scale.height);
      this.layout();
    });
  }

  update(_time: number, deltaMs: number) {
    const h = this.scale.height;
    const w = this.scale.width;
    const dt = deltaMs / 1000;
    for (const c of this.confetti) {
      c.g.x += c.vx * dt;
      c.g.y += c.vy * dt;
      c.g.rotation += c.vrot * dt;
      c.vy += 180 * dt;
      if (c.g.y > h + 20) {
        c.g.y = -20;
        c.g.x = Math.random() * w;
        c.vy = 60 + Math.random() * 80;
        c.vx = (Math.random() - 0.5) * 80;
      }
    }
  }

  private drawStarField(w: number, h: number) {
    const g = this.add.graphics();
    g.fillStyle(0xffffff, 0.6);
    for (let i = 0; i < 40; i++) {
      g.fillCircle(Math.random() * w, Math.random() * h * 0.8, Math.random() < 0.25 ? 1.6 : 1);
    }
  }

  private layout() {
    const w = this.scale.width;
    const h = this.scale.height;

    const title = this.add
      .text(w / 2, h * 0.12, 'YOU SAVED\nTHE WORLD!', {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '44px',
        color: '#ffd24a',
        fontStyle: 'bold',
        align: 'center',
        stroke: '#0a0f1f',
        strokeThickness: 8,
      })
      .setOrigin(0.5)
      .setAngle(-1);
    this.tweens.add({
      targets: title,
      scale: 1.06,
      angle: 1,
      duration: 1600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.drawHeroLineup(w, h);
    this.drawPlayAgainButton(w, h);
    this.spawnConfetti(w);
  }

  private drawHeroLineup(w: number, h: number) {
    const cols = HERO_ORDER.length;
    const availableW = w * 0.92;
    const slotW = Math.min(PORTRAIT_R * 2 + 16, Math.floor(availableW / cols));
    const totalW = slotW * cols;
    const startX = Math.floor((w - totalW) / 2 + slotW / 2);
    const y = Math.floor(h * 0.48);

    HERO_ORDER.forEach((id, i) => {
      const x = startX + i * slotW;
      const container = this.add.container(x, y);

      const ring = this.add.graphics();
      ring.fillStyle(0x121a32, 1);
      ring.lineStyle(4, 0xffd24a, 1);
      ring.fillCircle(0, 0, PORTRAIT_R);
      ring.strokeCircle(0, 0, PORTRAIT_R);
      container.add(ring);

      const set = heroFrameSet(id, 'idle');
      if (set.urls.length > 0 && this.textures.exists(frameKey(set.prefix, 0))) {
        const sprite = this.add.sprite(0, 4, frameKey(set.prefix, 0));
        const scale = (PORTRAIT_R * 1.55) / NATIVE_SPRITE;
        sprite.setScale(scale);
        container.add(sprite);
      } else {
        const placeholder = this.add.graphics();
        placeholder.fillStyle(0x2a3558, 1);
        placeholder.fillCircle(0, -4, PORTRAIT_R * 0.55);
        container.add(placeholder);
      }

      const label = this.add.text(0, PORTRAIT_R + 16, HEROES[id].displayName, {
        fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
        fontSize: '12px',
        color: '#f4f4f4',
        fontStyle: 'bold',
      }).setOrigin(0.5);
      container.add(label);

      this.tweens.add({
        targets: container,
        y: y - 10,
        duration: 900 + i * 80,
        yoyo: true,
        repeat: -1,
        delay: i * 90,
        ease: 'Sine.easeInOut',
      });
    });
  }

  private drawPlayAgainButton(w: number, h: number) {
    const cx = Math.floor(w / 2);
    const cy = Math.floor(h * 0.82);
    const btnContainer = this.add.container(cx, cy);

    const g = this.add.graphics();
    const btnR = 56;
    g.fillStyle(0x22c55e, 1);
    g.fillCircle(0, 0, btnR);
    g.lineStyle(4, 0x0a0f1f, 1);
    g.strokeCircle(0, 0, btnR);

    g.fillStyle(0x0a0f1f, 1);
    g.beginPath();
    g.moveTo(-btnR * 0.28, -btnR * 0.42);
    g.lineTo(btnR * 0.48, 0);
    g.lineTo(-btnR * 0.28, btnR * 0.42);
    g.closePath();
    g.fillPath();
    btnContainer.add(g);

    const caption = this.add.text(0, btnR + 24, 'Play again', {
      fontFamily: 'system-ui, -apple-system, Helvetica Neue, sans-serif',
      fontSize: '18px',
      color: '#f4f4f4',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    btnContainer.add(caption);

    const hit = this.add.zone(0, 0, btnR * 2.6, btnR * 2.6).setInteractive({ useHandCursor: true });
    hit.on('pointerup', () => this.scene.start('LevelMap'));
    btnContainer.add(hit);

    this.tweens.add({
      targets: btnContainer,
      scale: 1.08,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private spawnConfetti(w: number) {
    const colors = [0xfbbf24, 0xef4444, 0x3b82f6, 0x22c55e, 0xa855f7, 0xf97316];
    for (let i = 0; i < 40; i++) {
      const g = this.add.graphics();
      const color = colors[i % colors.length];
      g.fillStyle(color, 1);
      g.fillRect(-4, -2, 8, 4);
      g.setPosition(Math.random() * w, -20 - Math.random() * 200);
      this.confetti.push({
        g,
        vx: (Math.random() - 0.5) * 100,
        vy: 60 + Math.random() * 100,
        rot: 0,
        vrot: (Math.random() - 0.5) * 5,
      });
    }
  }
}
